<template>
  <div class="fit row wrap justify-start items-start content-start align-start q-pt-md">
    <!-- ExchangeAccounts Filter -->
    <q-select
      :model-value="state_search_params.exchange_account_id"
      outlined
      dense
      options-dense
      hide-bottom-space
      :options="transactions_filters.exchange_accounts"
      option-value="id"
      option-label="name"
      transition-show="none"
      transition-hide="none"
      popup-content-class="q-pa-sm"
      clearable
      multiple
      color="primary"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_exchange_accounts_selected.length > 0,
      }"
      @update:model-value="exchange_accounts_select"
    >
      <template v-slot:selected>
        <div class="">
          {{ filters_exchange_accounts_selected.length
            ? filters_exchange_accounts_selected.map(ea => ea.name).join(', ')
            : transactions_filters.exchange_accounts_label }}
        </div>
      </template>

      <template v-slot:prepend>
        <template v-if="filters_exchange_accounts_selected.length === 1">
          <q-avatar
            size="18px"
            square
          >
            <img
              :src="filters_exchange_accounts_selected[0].logo_url"
              :alt="filters_exchange_accounts_selected[0].exchange_label"
            >
          </q-avatar>
        </template>
      </template>

      <template v-slot:option="scope">
        <q-item
          v-bind="scope.itemProps"
          style="min-width: 320px"
          :class="{ 'text-accent text-bold': filters_exchange_accounts_selected.find((ea) => scope.opt.id == ea.id) }"
        >
          <q-item-section
            avatar
            class="q-mr-none q-pr-none"
          >
            <q-avatar
              v-if="scope.opt"
              size="22px"
              square
            >
              <img
                :src="scope.opt.logo_url"
                :alt="scope.opt"
              >
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
          <q-item-section
            side
            class="q-ml-lg"
          >
            <q-item-label class="text-bold">
              {{ scope.opt.balance }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <!-- No data -->
      <template v-slot:no-option>
        <q-item dense>
          <q-item-section>
            <q-item-label class="">
              {{ transactions_filters.exchange_accounts_empty }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- ImportSource filter -->
    <q-select
      v-if="transactions_filters.import_sources?.length > 0"
      :model-value="state_search_params.import_source_id"
      outlined
      dense
      options-dense
      transition-show="none"
      transition-hide="none"
      :options="[]"
      hide-bottom-space
      :prefix="transactions_filters.import_sources_label"
      option-label="label"
      option-value="id"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.import_source_id,
      }"
      input-class="text-capitalize"
      popup-content-class="transactions_import_source_selector_popup q-pa-sm"
      map-options
    >
      <template v-slot:selected-item>
        <q-badge
          v-if="filters_filled_count"
          class="text-bold menu-chip"
          style="font-size: 11px; border-radius: 2px; margin-left: 8px"
        >
          {{ filters_import_sources_selected.length }}
        </q-badge>
      </template>

      <template v-slot:append>
        <template v-if="state_search_params.import_source_id">
          <q-icon
            name="cancel"
            class="q-field__focusable-action"
            role="button"
            tabindex="0"
            :aria-label="$q.lang.label.clear"
            @mousedown.stop.prevent
            @click.stop="import_sources_clear"
          />
        </template>
      </template>

      <template v-slot:no-option>
        <div class="transactions_import_source_selector_panel">
          <div class="transactions_import_source_selector_content">
            <div class="transactions_import_source_selector_header q-pt-none q-px-none">
              <q-input
                v-model="filters_import_sources_search"
                outlined
                dense
                autofocus
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="filters_import_sources_search"
                    name="close"
                    class="cursor-pointer"
                    @click="filters_import_sources_search = ''"
                  />
                </template>
              </q-input>
            </div>
            <div class="transactions_import_source_selector_scroll q-pa-sm">
              <q-list dense>
                <template
                  v-for="(exchange_account) in filters_import_sources_filtered"
                  :key="exchange_account.id"
                >
                  <template v-if="exchange_account.import_sources.length > 0">
                    <q-item-label
                      header
                      class="text-bold"
                    >
                      <q-avatar
                        size="28px"
                        class="q-mr-sm"
                      >
                        <img
                          width="18px"
                          height="18px"
                          class="inline q-mr-xs"
                          :src="exchange_account.logo_url"
                        >
                      </q-avatar>

                      {{ exchange_account.name }}
                    </q-item-label>

                    <q-item
                      v-for="import_source in exchange_account.import_sources"
                      :key="import_source.id"
                      clickable
                      :active="filters_import_sources_selected.includes(import_source.id)"
                      :class="{ 'text-accent text-bold': filters_import_sources_selected.some((is) => is.id == import_source.id) }"
                      @click.stop="
                        filters_import_sources_search = '';
                        import_sources_select(import_source);
                      "
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ import_source.label }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-separator />
                  </template>
                </template>
              </q-list>
            </div>
          </div>
        </div>
      </template>
    </q-select>

    <!-- Assets Filter -->
    <q-select
      :model-value="state_search_params.asset_id"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      hide-bottom-space
      :options="filters_assets_filtered"
      :prefix="`${transactions_filters.assets_label}${filters_assets_selected.length > 0 ? ':' : ''}`"
      option-label="symbol"
      option-value="id"
      color="primary"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_assets_selected.length > 0,
      }"
      input-class="text-capitalize"
      popup-content-class="text-capitalize q-pa-sm"
      map-options
      clearable
      multiple
      @update:model-value="assets_select"
      @popup-hide="filters_assets_search = ''"
    >
      <template v-slot:selected>
        <div>
          {{ filters_assets_selected.map((a) => a.symbol).join(', ') }}
        </div>
      </template>

      <template v-slot:before-options>
        <div class="q-pb-sm">
          <q-input
            v-model="filters_assets_search"
            outlined
            dense
            :placeholder="transactions_filters.assets_placeholder"
            autofocus
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
            <template v-slot:append>
              <q-icon
                v-if="filters_assets_search"
                name="close"
                class="cursor-pointer"
                @click="filters_assets_search = ''"
              />
            </template>
          </q-input>
        </div>
      </template>

      <template v-slot:option="scope">
        <q-item
          v-bind="scope.itemProps"
          :class="{ 'text-accent text-bold': filters_assets_selected.find((o) => scope.opt.id == o.id) }"
        >
          <q-item-section
            avatar
            class="q-mr-none q-pr-none"
          >
            <img
              width="18px"
              height="18px"
              class="inline q-mr-xs"
              :alt="scope.opt.symbol"
              :src="scope.opt.logo_url"
            >
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
          <q-item-section
            side
            class="q-ml-lg"
          >
            <q-item-label class="text-bold ellipsis max-w[6ch]">
              {{ scope.opt.symbol }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <template v-slot:no-option>
        <div class="q-pb-sm">
          <q-input
            v-model="filters_assets_search"
            outlined
            dense
            :placeholder="transactions_filters.assets_placeholder"
            autofocus
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
            <template v-slot:append>
              <q-icon
                v-if="filters_assets_search"
                name="close"
                class="cursor-pointer"
                @click="filters_assets_search = ''"
              />
            </template>
          </q-input>
        </div>
        <q-item dense>
          <q-item-section>
            <q-item-label class="">
              {{ transactions_filters.assets_empty }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- TransactionKind Filter -->
    <q-select
      :model-value="state_search_params.kind"
      outlined
      dense
      options-dense
      transition-show="none"
      transition-hide="none"
      :options="[]"
      hide-bottom-space
      :prefix="`${transactions_filters.kinds_label}${filters_kinds_active ? ':' : ''}`"
      option-label="label"
      option-value="id"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_kinds_active,
      }"
      input-class="text-capitalize"
      popup-content-class="transactions_kind_selector_popup q-pa-sm"
      map-options
    >
      <template v-slot:append>
        <template v-if="filters_kinds_active">
          <q-icon
            name="cancel"
            class="q-field__focusable-action"
            role="button"
            tabindex="0"
            :aria-label="$q.lang.label.clear"
            @mousedown.stop.prevent
            @click.stop="kinds_clear"
          />
        </template>
      </template>
      <template v-slot:selected>
        <div class="">
          {{
            [
              (state_search_params.uncategorized == "true" ? transactions_filters.uncategorized_label : ''),
              ...(filters_kinds_selected.map(k => k.label)),
            ].filter((i) => i).join(', ')
          }}
        </div>
      </template>

      <template v-slot:no-option>
        <div class="transactions_kind_selector_panel">
          <div class="transactions_kind_selector_content">
            <div class="transactions_kind_selector_header q-pt-none q-px-none">
              <q-input
                v-model="filters_kinds_search"
                outlined
                dense
                :placeholder="transactions_filters.kinds_placeholder"
                autofocus
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="filters_kinds_search"
                    name="close"
                    class="cursor-pointer"
                    @click="filters_kinds_search = ''"
                  />
                </template>
              </q-input>
            </div>
            <div class="transactions_kind_selector_scroll q-pa-sm">
              <q-list dense>
                <q-item-label header>
                  <q-icon
                    name="mdi-circle"
                    class="q-mr-sm q-ml-sm"
                    color="grey-8"
                  />
                  {{ transactions_filters.kinds_status }}
                </q-item-label>

                <q-item
                  clickable
                  :class="{ 'text-accent text-bold': state_search_params.uncategorized == 'true' }"
                  @click="transactions_filter_uncategorized_toggle"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ transactions_filters.uncategorized_label }}
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-separator />

                <template
                  v-for="(category) in filters_kinds_filtered"
                  :key="category.name"
                >
                  <template v-if="category.kinds.length > 0">
                    <q-item-label
                      header
                      class="text-capitalize text-bold"
                    >
                      <q-avatar
                        :color="category.icon.color"
                        size="28px"
                        class="q-mr-sm"
                      >
                        <q-icon
                          :name="category.icon.class"
                          color="black"
                          size="18px"
                          class=""
                        />
                      </q-avatar>

                      {{ category.label }}
                    </q-item-label>

                    <q-item
                      v-for="kind in category.kinds"
                      :key="kind.id"
                      clickable
                      :active="filters_kinds_selected.includes(kind.id)"
                      :class="{ 'text-accent text-bold': filters_kinds_selected.some((k) => k.id == kind.id) }"
                      @click.stop="
                        filters_kinds_search = '';
                        kinds_select(kind);
                      "
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ kind.label }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-separator />
                  </template>
                </template>
              </q-list>
            </div>
          </div>
        </div>
      </template>
    </q-select>

    <!-- Date -->
    <q-select
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      :options="[]"
      hide-bottom-space
      :model-value="filters_date_range_label"
      :prefix="`${transactions_filters.dates_label}${filters_date_range_label ? ':' : ''}`"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_date_range_label,
      }"
      map-options
      clearable
      @clear="date_range_select(null)"
    >
      <template v-slot:no-option>
        <div
          class="row q-pa-none"
          style="max-width: 360px; width: 100%"
        >
          <q-date
            :model-value="filters_date_range"
            minimal
            flat
            range
            color="accent"
            text-color="default"
            class="fit"
            @update:model-value="date_range_select"
          />
        </div>

        <q-separator />

        <div
          v-if="workspace_settings.accounting_end_day"
          class="row justify-start q-pa-md q-pb-none"
        >
          <div class="col-12">
            <p class="text-bold">
              {{ transactions_filters.dates_fiscal_year }}
            </p>
          </div>
          <div class="col-12">
            <q-btn
              v-for="fy in transactions_filters.fiscal_years"
              :key="fy.label"
              class="q-mr-md"
              :label="fy.label"
              color="primary"
              :outline="date_range_fiscal_years_active(fy)"
              @click="date_range_fiscal_year_select(fy)"
            />
          </div>
        </div>

        <div class="row justify-end q-px-md q-pb-md">
          <div class="col-shrink">
            <q-btn
              v-close-popup
              :label="$q.lang.label.close"
              unelevated
              color="primary"
              flat
              no-caps
              class="btn-underline"
            />
          </div>
        </div>
      </template>
    </q-select>

    <!-- Inline additional filters -->
    <!-- Change rate filter -->
    <q-select
      v-if="state_search_params.hasOwnProperty('change_rate')"
      :model-value="state_search_params.change_rate"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      :options="transactions_filters.change_rates"
      hide-bottom-space
      :prefix="`${transactions_filters.change_rate_label}${state_search_params.change_rate ? ':' : ''}`"
      option-label="name"
      option-value="value"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.change_rate,
      }"
      popup-content-class="q-pa-sm"
      map-options
      clearable
      @update:model-value="change_rates_select"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section
            avatar
            class="q-sm-none q-mr-none q-pr-none"
          >
            <q-avatar
              v-if="scope.opt"
              size="22px"
              square
            >
              <q-icon
                :name="scope.opt.icon"
                size="18px"
                :color="scope.opt.icon_color"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- Balance filter -->
    <q-select
      v-if="state_search_params.hasOwnProperty('balance')"
      :model-value="state_search_params.balance"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      :options="transactions_filters.balances"
      hide-bottom-space
      :prefix="`${transactions_filters.balance_label}${state_search_params.balance ? ':' : ''}`"
      option-label="name"
      option-value="value"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.balance,
      }"
      popup-content-class="q-pa-sm"
      map-options
      clearable
      @update:model-value="balances_select"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section
            avatar
            class="q-sm-none q-mr-none q-pr-none"
          >
            <q-avatar
              v-if="scope.opt"
              size="22px"
              square
            >
              <q-icon
                :name="scope.opt.icon"
                size="18px"
                :color="scope.opt.icon_color"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- Direction/type filter -->
    <q-select
      v-if="state_search_params.hasOwnProperty('type')"
      :model-value="state_search_params.type"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      :options="transactions_filters.types"
      hide-bottom-space
      :prefix="`${transactions_filters.direction_label}${state_search_params.type ? ':' : ''}`"
      option-label="name"
      option-value="value"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.type,
      }"
      input-class="text-capitalize"
      popup-content-class="text-capitalize q-pa-sm"
      map-options
      clearable
      @update:model-value="types_select"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section
            avatar
            class="q-sm-none q-mr-none q-pr-none"
          >
            <q-avatar
              v-if="scope.opt"
              size="22px"
              square
            >
              <q-icon
                :name="scope.opt.icon"
                size="18px"
                :color="scope.opt.icon_color"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- Manual status filter -->
    <q-select
      v-if="state_search_params.hasOwnProperty('manual')"
      :model-value="state_search_params.manual"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      :options="transactions_filters.manual"
      hide-bottom-space
      :prefix="`${transactions_filters.manual_label}${state_search_params.manual ? ':' : ''}`"
      option-label="name"
      option-value="value"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.manual,
      }"
      input-class="text-capitalize"
      popup-content-class="text-capitalize q-pa-sm"
      map-options
      clearable
      @update:model-value="manual_select"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section
            avatar
            class="q-sm-none q-mr-none q-pr-none"
          >
            <q-avatar
              v-if="scope.opt"
              size="22px"
              square
            >
              <q-icon
                :name="scope.opt.icon"
                size="18px"
                :color="scope.opt.icon_color"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- Exact amount filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('amount')"
      :model-value="filters_amount"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.amount_label}:`"
      style="max-width: 180px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_amount,
      }"
      clearable
      debounce="500"
      @update:model-value="(v) => transactions_filter_add({ amount_min: v, amount_max: v })"
    />

    <!-- Minimum amount filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('amount_min')"
      :model-value="filters_amount_min"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.amount_min_label}:`"
      style="max-width: 160px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_amount_min,
      }"
      clearable
      debounce="500"
      @update:model-value="amount_min_update"
    />

    <!-- Maximum amount filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('amount_max')"
      :model-value="filters_amount_max"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.amount_max_label}:`"
      style="max-width: 160px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_amount_max,
      }"
      clearable
      debounce="500"
      @update:model-value="amount_max_update"
    />

    <!-- From address filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('from_address')"
      :model-value="state_search_params.from_address"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.sender_label}:`"
      style="max-width: 240px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.from_address,
      }"
      clearable
      debounce="500"
      @update:model-value="(v) => transactions_filter_add({ from_address: v })"
    >
      <template
        v-if="sortedAddressMappings.length > 0"
        v-slot:append
      >
        <q-btn
          flat
          round
          dense
          icon="las la-book"
        >
          <q-menu auto-close>
            <q-input
              v-model="searchFromAddress"
              outlined
              clearable
              :placeholder="$q.lang.label.search"
              dense
              class="q-pa-md"
              autofocus
            />
            <q-list style="width: 200px">
              <q-item
                v-for="address_mapping in filteredFromAddressMappings"
                :key="address_mapping.id"
                clickable
                @click="filterFromAddress = address_mapping.address"
              >
                <q-item-section>
                  {{ address_mapping.name }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </template>
    </q-input>

    <!-- To address filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('to_address')"
      :model-value="state_search_params.to_address"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.recipient_label}:`"
      style="max-width: 240px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.to_address,
      }"
      clearable
      debounce="500"
      @update:model-value="(v) => transactions_filter_add({ to_address: v })"
    >
      <template
        v-if="sortedAddressMappings.length > 0"
        v-slot:append
      >
        <q-btn
          flat
          round
          dense
          icon="las la-book"
        >
          <q-menu auto-close>
            <q-input
              v-model="searchToAddress"
              outlined
              clearable
              :placeholder="$q.lang.label.search"
              dense
              class="q-pa-md"
              autofocus
            />
            <q-list style="width: 200px">
              <q-item
                v-for="address_mapping in filteredToAddressMappings"
                :key="address_mapping.id"
                clickable
                @click="filterToAddress = address_mapping.address"
              >
                <q-item-section>
                  {{ address_mapping.name }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </template>
    </q-input>

    <!-- Note filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('note')"
      :model-value="state_search_params.note"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.note_label}:`"
      style="max-width: 160px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.note,
      }"
      clearable
      debounce="500"
      @update:model-value="(v) => transactions_filter_add({ note: v })"
    />

    <!-- Exchange asset filter -->
    <q-select
      v-if="state_search_params.hasOwnProperty('exchange_asset_id')"
      :model-value="state_search_params.exchange_asset_id"
      outlined
      dense
      transition-show="none"
      transition-hide="none"
      options-dense
      hide-bottom-space
      :options="filters_exchange_assets_combined_options"
      :prefix="`${transactions_filters.exchange_assets_label}${filters_exchange_assets_selected.length > 0 ? ':' : ''}`"
      option-label="exchange_symbol"
      option-value="id"
      color="primary"
      style="max-width: fit-content; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': filters_exchange_assets_selected.length > 0,
      }"
      popup-content-class="q-pa-sm"
      map-options
      clearable
      multiple
      use-input
      input-debounce="300"
      @filter="exchange_assets_filter"
      @update:model-value="exchange_assets_select"
    >
      <template v-slot:option="scope">
        <q-item
          v-bind="scope.itemProps"
          :class="{ 'text-accent text-bold': filters_exchange_assets_selected.find((o) => scope.opt.id == o.id) }"
        >
          <q-item-section
            avatar
            class="q-mr-none q-pr-none"
          >
            <img
              v-if="scope.opt.logo_url"
              width="18px"
              height="18px"
              class="inline q-mr-xs"
              :alt="scope.opt.exchange_symbol"
              :src="scope.opt.logo_url"
            >
            <q-icon
              v-else
              name="token"
              size="18px"
              color="grey-6"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ scope.opt.exchange_symbol }}
              <span
                v-if="scope.opt.asset_symbol"
                class="text-grey-6 text-xs"
              >({{ scope.opt.asset_symbol }})</span>
            </q-item-label>
            <q-item-label
              caption
              class="text-grey-6"
            >
              {{ scope.opt.exchange_name }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <template v-slot:no-option>
        <q-item dense>
          <q-item-section>
            <q-item-label class="">
              {{ filters_exchange_assets_search.length < 2 ? transactions_filters.exchange_assets_search_placeholder : transactions_filters.exchange_assets_empty }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- Transaction ID filter -->
    <q-input
      v-if="state_search_params.hasOwnProperty('id')"
      :model-value="state_search_params.id"
      hide-bottom-space
      outlined
      dense
      :prefix="`${transactions_filters.id_label}:`"
      style="max-width: 240px; width: 100%"
      class="select-filter q-mr-sm q-mb-sm"
      :class="{
        'select-filter-active': state_search_params.id,
      }"
      clearable
      debounce="500"
      @update:model-value="(v) => transactions_filter_add({ id: v })"
    />

    <q-btn
      v-if="filters_clear_show"
      flat
      icon="sym_o_filter_list_off"
      :label="i18n.transactions_filters_clear"
      no-caps
      color="primary"
      :title="i18n.transactions_filters_clear"
      class="q-mr-sm q-mb-sm"
      @click="transactions_filters_clear"
    />

    <q-btn
      outline
      :title="i18n.categorization_rules"
      no-caps
      color="primary"
      class="q-mr-sm q-mb-sm q-ml-auto"
    >
      <template v-slot:default>
        <div class="row items-center no-wrap">
          <q-icon
            name="sym_o_automation"
            size="18px"
          />
          <span
            v-if="$q.screen.gt.sm"
            class="q-ml-xs"
          >{{ i18n.categorization_rules_short }}</span>
        </div>

        <q-menu
          v-model="categorization_rules_menu_show"
          anchor="bottom right"
          self="top right"
          :offset="[0, 12]"
          class="transactions-filters-menu"
          @show="categorization_rules_load"
        >
          <div
            class="q-pa-lg filters-menu-panel"
            style="width: 640px; max-width: calc(100vw - 48px);"
          >
            <div class="row items-center q-mb-md">
              <div class="col text-subtitle2">
                {{ i18n.categorization_rules_title }}
              </div>
              <div class="col-auto">
                <div class="create-rule-btn-wrapper">
                  <q-btn
                    unelevated
                    round
                    color="primary"
                    icon="add"
                    class="create-rule-btn"
                    :aria-label="i18n.create_categorization_rule_short"
                    :disable="filters_filled_count == 0"
                    @click="handleCreateRule"
                  />
                  <q-tooltip
                    anchor="top middle"
                    self="bottom middle"
                    target=".create-rule-btn-wrapper .create-rule-btn"
                  >
                    {{ filters_filled_count > 0 ? i18n.create_categorization_rule_description : i18n.create_categorization_rule_disabled_tooltip }}
                  </q-tooltip>
                </div>
              </div>
            </div>

            <div class="text-secondary-caption q-mb-md">
              {{ i18n.categorization_rules_description }}
            </div>

            <q-list
              v-if="categorization_rules && categorization_rules.length > 0"
              class="q-mb-sm categorization-rules-list"
            >
              <template
                v-for="(rule, index) in categorization_rules"
                :key="rule.id"
              >
                <q-item class="q-pa-md categorization-rule-item">
                  <q-item-section>
                    <div class="categorization-rule-flow row items-center no-wrap">
                      <q-chip
                        size="12px"
                        class="q-ma-none flat square categorization-rule-name-chip"
                      >
                        {{ rule.name }}
                      </q-chip>
                      <q-icon
                        name="sym_o_arrow_forward"
                        size="18px"
                        class="text-primary"
                      />
                      <q-chip
                        v-if="rule.transaction_kind_icon"
                        :color="rule.transaction_kind_icon.color"
                        :text-color="$q.dark.isActive ? rule.transaction_kind_icon.color : 'black'"
                        flat
                        square
                        size="14px"
                        class="q-ma-none transaction-kind-chip categorization-rule-kind-chip"
                        :icon="rule.transaction_kind_icon.class"
                      >
                        {{ rule.transaction_kind_name }}
                      </q-chip>
                      <q-chip
                        v-else
                        dense
                        square
                        class="q-ma-none categorization-rule-kind-chip"
                        size="14px"
                      >
                        {{ rule.transaction_kind_name }}
                      </q-chip>
                    </div>
                  </q-item-section>
                  <q-item-section
                    side
                    class="categorization-rule-actions"
                  >
                    <q-btn
                      flat
                      round
                      dense
                      color="primary"
                      icon="sym_o_filter_list"
                      :aria-label="i18n.categorization_rule_preview"
                      @click="handleRulePreview(rule)"
                    >
                      <q-tooltip>{{ i18n.categorization_rule_preview }}</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      color="primary"
                      icon="sym_o_play_arrow"
                      :aria-label="i18n.categorization_rule_apply"
                      @click="handleRuleApply(rule.id)"
                    >
                      <q-tooltip>{{ i18n.categorization_rule_apply }}</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      color="primary"
                      icon="sym_o_delete"
                      :aria-label="i18n.categorization_rule_delete"
                      @click="handleRuleDelete(rule.id)"
                    >
                      <q-tooltip>{{ i18n.categorization_rule_delete }}</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </q-item>
                <div
                  v-if="index < categorization_rules.length - 1"
                  :key="`divider-${rule.id}`"
                  class="transactions-settings-dialog__divider categorization-rules-divider"
                />
              </template>
            </q-list>

            <div
              v-else
              class="text-center text-muted q-pa-xl"
            >
              {{ i18n.no_categorization_rules }}
            </div>

            <div class="row items-center justify-between q-mt-md">
              <div>
                <q-btn
                  v-if="categorization_rules && categorization_rules.length > 0"
                  unelevated
                  outline
                  color="primary"
                  icon="sym_o_fast_forward"
                  no-caps
                  :label="i18n.categorization_rule_apply_all"
                  @click="handleApplyAllRules"
                />
              </div>
              <div>
                <q-btn
                  v-close-popup
                  flat
                  dense
                  no-caps
                  color="primary"
                  :label="$q.lang.label.close"
                />
              </div>
            </div>
          </div>
        </q-menu>
      </template>
    </q-btn>

    <q-btn
      outline
      :title="i18n.transactions_filters_filters"
      no-caps
      color="primary"
      class="q-mr-sm q-mb-sm"
    >
      <template v-slot:default>
        <div class="row items-center no-wrap">
          <q-icon
            name="mdi-filter-variant"
            size="18px"
          />
          <span
            v-if="$q.screen.gt.sm"
            class="q-ml-xs"
          >{{ i18n.transactions_filters_filters }}</span>
          <q-badge
            v-if="filters_filled_count"
            class="text-bold menu-chip"
            style="font-size: 11px; border-radius: 2px; margin-left: 8px"
          >
            {{ filters_filled_count }}
          </q-badge>
        </div>

        <q-menu
          v-model="filters_menu_show"
          anchor="bottom right"
          self="top right"
          :offset="[0, 12]"
          class="transactions-filters-menu"
        >
          <div
            class="q-pa-lg filters-menu-panel"
            style="width: 720px; max-width: calc(100vw - 48px);"
          >
            <div class="text-subtitle2 q-mb-md">
              {{ i18n.transactions_filters_filters }}
            </div>

            <div class="row q-col-gutter-md">
              <!-- Menu: ExchangeAccounts Filter -->
              <div class="col-12 col-md-6">
                <q-select
                  :model-value="state_search_params.exchange_account_id"
                  outlined
                  dense
                  options-dense
                  hide-bottom-space
                  :options="transactions_filters.exchange_accounts"
                  option-value="id"
                  option-label="name"
                  transition-show="none"
                  transition-hide="none"
                  popup-content-class="q-pa-sm"
                  clearable
                  multiple
                  color="primary"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_exchange_accounts_selected.length > 0,
                  }"
                  @update:model-value="exchange_accounts_select"
                >
                  <template v-slot:selected>
                    <div class="">
                      {{ filters_exchange_accounts_selected.length
                        ? filters_exchange_accounts_selected.map(ea => ea.name).join(', ')
                        : transactions_filters.exchange_accounts_label }}
                    </div>
                  </template>

                  <template v-slot:prepend>
                    <template v-if="filters_exchange_accounts_selected.length === 1">
                      <q-avatar
                        size="18px"
                        square
                      >
                        <img
                          :src="filters_exchange_accounts_selected[0].logo_url"
                          :alt="filters_exchange_accounts_selected[0].exchange_label"
                        >
                      </q-avatar>
                    </template>
                  </template>

                  <template v-slot:option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      style="min-width: 320px"
                      :class="{ 'text-accent text-bold': filters_exchange_accounts_selected.find((ea) => scope.opt.id == ea.id) }"
                    >
                      <q-item-section
                        avatar
                        class="q-mr-none q-pr-none"
                      >
                        <q-avatar
                          v-if="scope.opt"
                          size="22px"
                          square
                        >
                          <img
                            :src="scope.opt.logo_url"
                            :alt="scope.opt"
                          >
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                      <q-item-section
                        side
                        class="q-ml-lg"
                      >
                        <q-item-label class="text-bold">
                          {{ scope.opt.balance }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>

                  <template v-slot:no-option>
                    <q-item dense>
                      <q-item-section>
                        <q-item-label class="">
                          {{ transactions_filters.exchange_accounts_empty }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>

              <!-- Menu: ImportSource filter -->
              <div
                v-if="transactions_filters.import_sources.length > 0"
                class="col-12 col-md-6"
              >
                <q-select
                  :model-value="state_search_params.import_source_id"
                  outlined
                  dense
                  options-dense
                  transition-show="none"
                  transition-hide="none"
                  :options="[]"
                  hide-bottom-space
                  :prefix="transactions_filters.import_sources_label"
                  option-label="label"
                  option-value="id"
                  style="width: 100%"
                  class="select-filter q-mr-sm q-mb-sm"
                  :class="{
                    'select-filter-active': state_search_params.import_source_id,
                  }"
                  input-class="text-capitalize"
                  popup-content-class="transactions_import_source_selector_popup q-pa-sm"
                  map-options
                >
                  <template v-slot:selected-item>
                    <q-badge
                      v-if="filters_filled_count"
                      class="text-bold menu-chip"
                      style="font-size: 11px; border-radius: 2px; margin-left: 8px"
                    >
                      {{ filters_import_sources_selected.length }}
                    </q-badge>
                  </template>

                  <template v-slot:append>
                    <template v-if="state_search_params.import_source_id">
                      <q-icon
                        name="cancel"
                        class="q-field__focusable-action"
                        role="button"
                        tabindex="0"
                        :aria-label="$q.lang.label.clear"
                        @mousedown.stop.prevent
                        @click.stop="import_sources_clear"
                      />
                    </template>
                  </template>

                  <template v-slot:no-option>
                    <div class="transactions_import_source_selector_panel">
                      <div class="transactions_import_source_selector_content">
                        <div class="transactions_import_source_selector_header q-pt-none q-px-none">
                          <q-input
                            v-model="filters_import_sources_search"
                            outlined
                            dense
                            autofocus
                          >
                            <template v-slot:prepend>
                              <q-icon name="search" />
                            </template>
                            <template v-slot:append>
                              <q-icon
                                v-if="filters_import_sources_search"
                                name="close"
                                class="cursor-pointer"
                                @click="filters_import_sources_search = ''"
                              />
                            </template>
                          </q-input>
                        </div>
                        <div class="transactions_import_source_selector_scroll q-pa-sm">
                          <q-list dense>
                            <template
                              v-for="(exchange_account) in filters_import_sources_filtered"
                              :key="exchange_account.id"
                            >
                              <template v-if="exchange_account.import_sources.length > 0">
                                <q-item-label
                                  header
                                  class="text-bold"
                                >
                                  <q-avatar
                                    size="28px"
                                    class="q-mr-sm"
                                  >
                                    <img
                                      width="18px"
                                      height="18px"
                                      class="inline q-mr-xs"
                                      :src="exchange_account.logo_url"
                                    >
                                  </q-avatar>

                                  {{ exchange_account.name }}
                                </q-item-label>

                                <q-item
                                  v-for="import_source in exchange_account.import_sources"
                                  :key="import_source.id"
                                  clickable
                                  :active="filters_import_sources_selected.includes(import_source.id)"
                                  :class="{ 'text-accent text-bold': filters_import_sources_selected.some((is) => is.id == import_source.id) }"
                                  @click.stop="
                                    filters_import_sources_search = '';
                                    import_sources_select(import_source);
                                  "
                                >
                                  <q-item-section>
                                    <q-item-label>
                                      {{ import_source.label }}
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-separator />
                              </template>
                            </template>
                          </q-list>
                        </div>
                      </div>
                    </div>
                  </template>
                </q-select>
              </div>

              <!-- Menu: Assets Filter -->
              <div class="col-12 col-md-6">
                <q-select
                  :model-value="state_search_params.asset_id"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  hide-bottom-space
                  :options="filters_assets_filtered"
                  :prefix="`${transactions_filters.assets_label}${filters_assets_selected.length > 0 ? ':' : ''}`"
                  option-label="symbol"
                  option-value="id"
                  color="primary"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_assets_selected.length > 0,
                  }"
                  input-class="text-capitalize"
                  popup-content-class="text-capitalize q-pa-sm"
                  map-options
                  clearable
                  multiple
                  @update:model-value="assets_select"
                  @popup-hide="filters_assets_search = ''"
                >
                  <template v-slot:selected>
                    <div>
                      {{ filters_assets_selected.map((a) => a.symbol).join(', ') }}
                    </div>
                  </template>

                  <template v-slot:before-options>
                    <div class="q-pb-sm">
                      <q-input
                        v-model="filters_assets_search"
                        outlined
                        dense
                        :placeholder="transactions_filters.assets_placeholder"
                        autofocus
                      >
                        <template v-slot:prepend>
                          <q-icon name="search" />
                        </template>
                        <template v-slot:append>
                          <q-icon
                            v-if="filters_assets_search"
                            name="close"
                            class="cursor-pointer"
                            @click="filters_assets_search = ''"
                          />
                        </template>
                      </q-input>
                    </div>
                  </template>

                  <template v-slot:option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      :class="{ 'text-accent text-bold': filters_assets_selected.find((o) => scope.opt.id == o.id) }"
                    >
                      <q-item-section
                        avatar
                        class="q-mr-none q-pr-none"
                      >
                        <img
                          width="18px"
                          height="18px"
                          class="inline q-mr-xs"
                          :alt="scope.opt.symbol"
                          :src="scope.opt.logo_url"
                        >
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                      <q-item-section
                        side
                        class="q-ml-lg"
                      >
                        <q-item-label class="text-bold ellipsis max-w[6ch]">
                          {{ scope.opt.symbol }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>

                  <template v-slot:no-option>
                    <div class="q-pb-sm">
                      <q-input
                        v-model="filters_assets_search"
                        outlined
                        dense
                        :placeholder="transactions_filters.assets_placeholder"
                        autofocus
                      >
                        <template v-slot:prepend>
                          <q-icon name="search" />
                        </template>
                        <template v-slot:append>
                          <q-icon
                            v-if="filters_assets_search"
                            name="close"
                            class="cursor-pointer"
                            @click="filters_assets_search = ''"
                          />
                        </template>
                      </q-input>
                    </div>
                    <q-item dense>
                      <q-item-section>
                        <q-item-label class="">
                          {{ transactions_filters.assets_empty }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>

              <!-- Menu: TransactionKind Filter -->
              <div class="col-12 col-md-6">
                <q-select
                  :model-value="state_search_params.kind"
                  outlined
                  dense
                  options-dense
                  transition-show="none"
                  transition-hide="none"
                  :options="[]"
                  hide-bottom-space
                  :prefix="`${transactions_filters.kinds_label}${filters_kinds_active ? ':' : ''}`"
                  option-label="label"
                  option-value="id"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_kinds_active,
                  }"
                  input-class="text-capitalize"
                  popup-content-class="transactions_kind_selector_popup q-pa-sm"
                  map-options
                >
                  <template v-slot:append>
                    <template v-if="filters_kinds_active">
                      <q-icon
                        name="cancel"
                        class="q-field__focusable-action"
                        role="button"
                        tabindex="0"
                        :aria-label="$q.lang.label.clear"
                        @mousedown.stop.prevent
                        @click.stop="kinds_clear"
                      />
                    </template>
                  </template>
                  <template v-slot:selected>
                    <div class="">
                      {{
                        [
                          (state_search_params.uncategorized == "true" ? transactions_filters.uncategorized_label : ''),
                          ...(filters_kinds_selected.map(k => k.label)),
                        ].filter((i) => i).join(', ')
                      }}
                    </div>
                  </template>

                  <template v-slot:no-option>
                    <div class="transactions_kind_selector_panel">
                      <div class="transactions_kind_selector_content">
                        <div class="transactions_kind_selector_header q-pt-none q-px-none">
                          <q-input
                            v-model="filters_kinds_search"
                            outlined
                            dense
                            :placeholder="transactions_filters.kinds_placeholder"
                            autofocus
                          >
                            <template v-slot:prepend>
                              <q-icon name="search" />
                            </template>
                            <template v-slot:append>
                              <q-icon
                                v-if="filters_kinds_search"
                                name="close"
                                class="cursor-pointer"
                                @click="filters_kinds_search = ''"
                              />
                            </template>
                          </q-input>
                        </div>
                        <div class="transactions_kind_selector_scroll q-pa-sm">
                          <q-list dense>
                            <q-item-label header>
                              <q-icon
                                name="mdi-circle"
                                class="q-mr-sm q-ml-sm"
                                color="grey-8"
                              />
                              {{ transactions_filters.kinds_status }}
                            </q-item-label>

                            <q-item
                              clickable
                              :class="{ 'text-accent text-bold': state_search_params.uncategorized == 'true' }"
                              @click="transactions_filter_uncategorized_toggle"
                            >
                              <q-item-section>
                                <q-item-label>
                                  {{ transactions_filters.uncategorized_label }}
                                </q-item-label>
                              </q-item-section>
                            </q-item>

                            <q-separator />

                            <template
                              v-for="(category) in filters_kinds_filtered"
                              :key="category.name"
                            >
                              <template v-if="category.kinds.length > 0">
                                <q-item-label
                                  header
                                  class="text-capitalize text-bold"
                                >
                                  <q-avatar
                                    :color="category.icon.color"
                                    size="28px"
                                    class="q-mr-sm"
                                  >
                                    <q-icon
                                      :name="category.icon.class"
                                      color="black"
                                      size="18px"
                                      class=""
                                    />
                                  </q-avatar>

                                  {{ category.label }}
                                </q-item-label>

                                <q-item
                                  v-for="kind in category.kinds"
                                  :key="kind.id"
                                  clickable
                                  :active="filters_kinds_selected.includes(kind.id)"
                                  :class="{ 'text-accent text-bold': filters_kinds_selected.some((k) => k.id == kind.id) }"
                                  @click.stop="
                                    filters_kinds_search = '';
                                    kinds_select(kind);
                                  "
                                >
                                  <q-item-section>
                                    <q-item-label>
                                      {{ kind.label }}
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-separator />
                              </template>
                            </template>
                          </q-list>
                        </div>
                      </div>
                    </div>
                  </template>
                </q-select>
              </div>

              <!-- Menu: Date Filter -->
              <div class="col-12 col-md-6">
                <q-select
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  :options="[]"
                  hide-bottom-space
                  :model-value="filters_date_range_label"
                  :prefix="`${transactions_filters.dates_label}${filters_date_range_label ? ':' : ''}`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_date_range_label,
                  }"
                  map-options
                  clearable
                  @clear="date_range_select(null)"
                >
                  <template v-slot:no-option>
                    <div
                      class="row q-pa-none"
                      style="max-width: 360px; width: 100%"
                    >
                      <q-date
                        :model-value="filters_date_range"
                        minimal
                        flat
                        range
                        color="accent"
                        text-color="default"
                        class="fit"
                        @update:model-value="date_range_select"
                      />
                    </div>

                    <q-separator />

                    <div
                      v-if="workspace_settings.accounting_end_day"
                      class="row justify-start q-pa-md q-pb-none"
                    >
                      <div class="col-12">
                        <p class="text-bold">
                          {{ transactions_filters.dates_fiscal_year }}
                        </p>
                      </div>
                      <div class="col-12">
                        <q-btn
                          v-for="fy in transactions_filters.fiscal_years"
                          :key="fy.label"
                          class="q-mr-md"
                          :label="fy.label"
                          color="primary"
                          :outline="date_range_fiscal_years_active(fy)"
                          @click="date_range_fiscal_year_select(fy)"
                        />
                      </div>
                    </div>

                    <div class="row justify-end q-px-md q-pb-md">
                      <div class="col-shrink">
                        <q-btn
                          v-close-popup
                          :label="$q.lang.label.close"
                          unelevated
                          color="primary"
                          flat
                          no-caps
                          class="btn-underline"
                        />
                      </div>
                    </div>
                  </template>
                </q-select>
              </div>


              <div class="col-12 col-md-6">
                <!-- Menu: change rate filter -->
                <q-select
                  :model-value="state_search_params.change_rate"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  :options="transactions_filters.change_rates"
                  hide-bottom-space
                  :prefix="`${transactions_filters.change_rate_label}${state_search_params.change_rate ? ':' : ''}`"
                  option-label="name"
                  option-value="value"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.change_rate,
                  }"
                  popup-content-class="q-pa-sm"
                  map-options
                  clearable
                  @update:model-value="change_rates_select"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section
                        avatar
                        class="q-sm-none q-mr-none q-pr-none"
                      >
                        <q-avatar
                          v-if="scope.opt"
                          size="22px"
                          square
                        >
                          <q-icon
                            :name="scope.opt.icon"
                            size="18px"
                            :color="scope.opt.icon_color"
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>
              <div class="col-12 col-md-6">
                <!-- Menu: balance filter -->
                <q-select
                  :model-value="state_search_params.balance"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  :options="transactions_filters.balances"
                  hide-bottom-space
                  :prefix="`${transactions_filters.balance_label}${state_search_params.balance ? ':' : ''}`"
                  option-label="name"
                  option-value="value"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.balance,
                  }"
                  popup-content-class="q-pa-sm"
                  map-options
                  clearable
                  @update:model-value="balances_select"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section
                        avatar
                        class="q-sm-none q-mr-none q-pr-none"
                      >
                        <q-avatar
                          v-if="scope.opt"
                          size="22px"
                          square
                        >
                          <q-icon
                            :name="scope.opt.icon"
                            size="18px"
                            :color="scope.opt.icon_color"
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>
              <div class="col-12 col-md-6">
                <!-- Menu: direction/type filter -->
                <q-select
                  :model-value="state_search_params.type"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  :options="transactions_filters.types"
                  hide-bottom-space
                  :prefix="`${transactions_filters.direction_label}${state_search_params.type ? ':' : ''}`"
                  option-label="name"
                  option-value="value"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.type,
                  }"
                  input-class="text-capitalize"
                  popup-content-class="text-capitalize q-pa-sm"
                  map-options
                  clearable
                  @update:model-value="types_select"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section
                        avatar
                        class="q-sm-none q-mr-none q-pr-none"
                      >
                        <q-avatar
                          v-if="scope.opt"
                          size="22px"
                          square
                        >
                          <q-icon
                            :name="scope.opt.icon"
                            size="18px"
                            :color="scope.opt.icon_color"
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>

              <div class="col-12 col-md-6">
                <!-- Menu: manual status filter -->
                <q-select
                  :model-value="state_search_params.manual"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  :options="transactions_filters.manual"
                  hide-bottom-space
                  :prefix="`${transactions_filters.manual_label}${state_search_params.manual ? ':' : ''}`"
                  option-label="name"
                  option-value="value"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.manual,
                  }"
                  input-class="text-capitalize"
                  popup-content-class="text-capitalize q-pa-sm"
                  map-options
                  clearable
                  @update:model-value="manual_select"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section
                        avatar
                        class="q-sm-none q-mr-none q-pr-none"
                      >
                        <q-avatar
                          v-if="scope.opt"
                          size="22px"
                          square
                        >
                          <q-icon
                            :name="scope.opt.icon"
                            size="18px"
                            :color="scope.opt.icon_color"
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ scope.opt.name }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>
              <div class="col-12 col-md-6">
                <!-- Menu: exchange asset filter -->
                <q-select
                  :model-value="state_search_params.exchange_asset_id"
                  outlined
                  dense
                  transition-show="none"
                  transition-hide="none"
                  options-dense
                  hide-bottom-space
                  :options="filters_exchange_assets_combined_options"
                  :prefix="`${transactions_filters.exchange_assets_label}${filters_exchange_assets_selected.length > 0 ? ':' : ''}`"
                  option-label="exchange_symbol"
                  option-value="id"
                  color="primary"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_exchange_assets_selected.length > 0,
                  }"
                  popup-content-class="q-pa-sm"
                  map-options
                  clearable
                  multiple
                  use-input
                  input-debounce="300"
                  @filter="exchange_assets_filter"
                  @update:model-value="exchange_assets_select"
                >
                  <template v-slot:option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      :class="{ 'text-accent text-bold': filters_exchange_assets_selected.find((o) => scope.opt.id == o.id) }"
                    >
                      <q-item-section
                        avatar
                        class="q-mr-none q-pr-none"
                      >
                        <img
                          v-if="scope.opt.logo_url"
                          width="18px"
                          height="18px"
                          class="inline q-mr-xs"
                          :alt="scope.opt.exchange_symbol"
                          :src="scope.opt.logo_url"
                        >
                        <q-icon
                          v-else
                          name="token"
                          size="18px"
                          color="grey-6"
                        />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>
                          {{ scope.opt.exchange_symbol }}
                          <span
                            v-if="scope.opt.asset_symbol"
                            class="text-grey-6 text-xs"
                          >({{ scope.opt.asset_symbol }})</span>
                        </q-item-label>
                        <q-item-label
                          caption
                          class="text-grey-6"
                        >
                          {{ scope.opt.exchange_name }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>

                  <template v-slot:no-option>
                    <q-item dense>
                      <q-item-section>
                        <q-item-label class="">
                          {{ filters_exchange_assets_search.length < 2 ? transactions_filters.exchange_assets_search_placeholder : transactions_filters.exchange_assets_empty }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>

              <div class="col-12 col-md-6">
                <!-- Menu: exact amount filter -->
                <q-input
                  :model-value="filters_amount"
                  hide-bottom-space
                  outlined
                  dense
                  :prefix="`${transactions_filters.amount_label}:`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': filters_amount,
                  }"
                  clearable
                  debounce="500"
                  @update:model-value="(v) => transactions_filter_add({ amount_min: v, amount_max: v })"
                />
              </div>
              <div class="col-12 col-md-6">
                <div class="row q-col-gutter-sm">
                  <div class="col-12 col-sm-6">
                    <!-- Menu: minimum amount filter -->
                    <q-input
                      :model-value="filters_amount_min"
                      hide-bottom-space
                      outlined
                      dense
                      :prefix="`${transactions_filters.amount_min_label}:`"
                      style="width: 100%"
                      class="select-filter full-width"
                      :class="{
                        'select-filter-active': filters_amount_min,
                      }"
                      clearable
                      debounce="500"
                      @update:model-value="amount_min_update"
                    />
                  </div>
                  <div class="col-12 col-sm-6">
                    <!-- Menu: maximum amount filter -->
                    <q-input
                      :model-value="filters_amount_max"
                      hide-bottom-space
                      outlined
                      dense
                      :prefix="`${transactions_filters.amount_max_label}:`"
                      style="width: 100%"
                      class="select-filter full-width"
                      :class="{
                        'select-filter-active': filters_amount_max,
                      }"
                      clearable
                      debounce="500"
                      @update:model-value="amount_max_update"
                    />
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-6">
                <!-- Menu: from address filter -->
                <q-input
                  :model-value="state_search_params.from_address"
                  hide-bottom-space
                  outlined
                  dense
                  :prefix="`${transactions_filters.sender_label}:`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.from_address,
                  }"
                  clearable
                  debounce="500"
                  @update:model-value="(v) => transactions_filter_add({ from_address: v })"
                >
                  <template
                    v-if="sortedAddressMappings.length > 0"
                    v-slot:append
                  >
                    <q-btn
                      flat
                      round
                      dense
                      icon="las la-book"
                    >
                      <q-menu auto-close>
                        <q-input
                          v-model="searchFromAddress"
                          outlined
                          clearable
                          :placeholder="$q.lang.label.search"
                          dense
                          class="q-pa-md"
                          autofocus
                        />
                        <q-list style="width: 200px">
                          <q-item
                            v-for="address_mapping in filteredFromAddressMappings"
                            :key="address_mapping.id"
                            clickable
                            @click="filterFromAddress = address_mapping.address"
                          >
                            <q-item-section>
                              {{ address_mapping.name }}
                            </q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                  </template>
                </q-input>
              </div>
              <div class="col-12 col-md-6">
                <!-- Menu: to address filter -->
                <q-input
                  :model-value="state_search_params.to_address"
                  hide-bottom-space
                  outlined
                  dense
                  :prefix="`${transactions_filters.recipient_label}:`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.to_address,
                  }"
                  clearable
                  debounce="500"
                  @update:model-value="(v) => transactions_filter_add({ to_address: v })"
                >
                  <template
                    v-if="sortedAddressMappings.length > 0"
                    v-slot:append
                  >
                    <q-btn
                      flat
                      round
                      dense
                      icon="las la-book"
                    >
                      <q-menu auto-close>
                        <q-input
                          v-model="searchToAddress"
                          outlined
                          clearable
                          :placeholder="$q.lang.label.search"
                          dense
                          class="q-pa-md"
                          autofocus
                        />
                        <q-list style="width: 200px">
                          <q-item
                            v-for="address_mapping in filteredToAddressMappings"
                            :key="address_mapping.id"
                            clickable
                            @click="filterToAddress = address_mapping.address"
                          >
                            <q-item-section>
                              {{ address_mapping.name }}
                            </q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                  </template>
                </q-input>
              </div>

              <div class="col-12 col-md-6">
                <!-- Menu: transaction ID filter -->
                <q-input
                  :model-value="state_search_params.id"
                  hide-bottom-space
                  outlined
                  dense
                  :prefix="`${transactions_filters.id_label}:`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.id,
                  }"
                  clearable
                  debounce="500"
                  @update:model-value="(v) => transactions_filter_add({ id: v })"
                />
              </div>

              <div class="col-12 col-md-6">
                <!-- Menu: note filter -->
                <q-input
                  :model-value="state_search_params.note"
                  hide-bottom-space
                  outlined
                  dense
                  :prefix="`${transactions_filters.note_label}:`"
                  style="width: 100%"
                  class="select-filter full-width"
                  :class="{
                    'select-filter-active': state_search_params.note,
                  }"
                  clearable
                  debounce="500"
                  @update:model-value="(v) => transactions_filter_add({ note: v })"
                />
              </div>
            </div>

            <div class="row justify-end q-gutter-sm q-mt-sm">
              <q-btn
                flat
                dense
                no-caps
                color="primary"
                :label="i18n.transactions_filters_clear"
                :disable="!filters_clear_show"
                @click="transactions_filters_clear"
              />
              <q-btn
                v-close-popup
                flat
                dense
                no-caps
                color="primary"
                :label="$q.lang.label.close"
              />
            </div>
          </div>
        </q-menu>
      </template>
    </q-btn>
  </div>
</template>

<script>
import { date as qdate } from 'quasar';
import { toRaw } from 'vue';

export default {
  name: 'TransactionsFilters',
  props: {
    transactions_filters: {
      type: Object,
      required: true,
    },
    workspace_settings: {
      type: Object,
      required: true,
    },
    i18n: {
      type: Object,
      required: true,
    },
    state_search_params: {
      type: Object,
      required: true,
    },
    filters_clear_show: {
      type: Boolean,
      required: true,
    },
    filters_filled_count: {
      type: Number,
      required: true,
    },
    filters_exchange_accounts_selected: {
      type: Array,
      required: true,
    },
    filters_assets_selected: {
      type: Array,
      required: true,
    },
    filters_kinds_selected: {
      type: Array,
      required: true,
    },
    filters_import_sources_selected: {
      type: Array,
      required: true,
    },
    transactions_filter_add: {
      type: Function,
      required: true,
    },
    transactions_filters_clear: {
      type: Function,
      required: true,
    },
    transactions_filter_uncategorized_toggle: {
      type: Function,
      required: true,
    },
    create_categorization_rule_dialog_open: {
      type: Function,
      required: true,
    },
    categorization_rules: {
      type: Array,
      required: true,
    },
    categorization_rules_load: {
      type: Function,
      required: true,
    },
    categorization_rules_apply_all: {
      type: Function,
      required: true,
    },
    categorization_rule_preview: {
      type: Function,
      required: true,
    },
    categorization_rule_apply: {
      type: Function,
      required: true,
    },
    categorization_rule_delete: {
      type: Function,
      required: true,
    },
    address_mappings_map: {
      type: Function,
      required: true,
    },
    address_mappings_dialog_open: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      filters_assets_search: '',
      filters_kinds_search: '',
      filters_import_sources_search: '',
      filters_exchange_assets_search: '',
      filters_exchange_assets_options: [],
      filters_exchange_assets_loading: false,
      filters_menu_show: false,
      categorization_rules_menu_show: false,
      filterFromAddress: '',
      filterToAddress: '',
      searchFromAddress: '',
      searchToAddress: '',
    };
  },
  computed: {
    filters_import_sources_filtered() {
      let exchange_accounts = [];

      this.transactions_filters.import_sources.forEach((ea) => {
        let exchange_account = structuredClone(toRaw(ea));
        let found = false;
        exchange_account.import_sources = [];
        ea.import_sources.forEach((is) => {
          if (!this.filters_import_sources_search || is.label.toLowerCase().includes(this.filters_import_sources_search.toLowerCase())) {
            exchange_account.import_sources.push(is);
          }
          found = true;
        });
        if (found) {
          exchange_accounts.push(exchange_account);
        }
      });

      return exchange_accounts;
    },
    filters_assets_filtered() {
      if (!this.filters_assets_search) {
        return this.transactions_filters.assets;
      }
      const search = this.filters_assets_search.toLowerCase();
      return this.transactions_filters.assets.filter((a) => (
        (a.name || '').toLowerCase().includes(search) || (a.symbol || '').toLowerCase().includes(search)
      ));
    },
    filters_kinds_filtered() {
      let categories = [];
      let categories_index = -1;

      this.transactions_filters.kinds.forEach((tk) => {
        let category = categories[categories_index];
        if (category?.name != tk.category_name) {
          categories_index += 1;
          category = {
            name: tk.category_name,
            icon: tk.icon,
            label: tk.category_label,
            kinds: [],
          };
          categories[categories_index] = category;
        }

        if (!this.filters_kinds_search || tk.label.toLowerCase().includes(this.filters_kinds_search.toLowerCase())) {
          category.kinds.push(tk);
        }
      });

      return categories;
    },
    filters_kinds_active() {
      return this.filters_kinds_selected.length > 0 || this.state_search_params.uncategorized == 'true';
    },
    filters_date_range() {
      const from = this.state_search_params.date_from;
      const to = this.state_search_params.date_to;
      if (from != to) {
        return {
          from: from,
          to: to,
        };
      } else {
        return from;
      }
    },
    filters_date_range_label() {
      const from = this.state_search_params.date_from;
      const to = this.state_search_params.date_to;
      const date_format = "DD/MM/YY";
      if (!from) {
        return;
      }
      let from_str = qdate.formatDate(from, date_format);
      if (from_str == undefined) {
        from_str = '…';
      }
      let to_str = qdate.formatDate(to, date_format);
      if (to_str == undefined) {
        to_str = '…';
      }
      if (from != to) {
        return `${from_str} - ${to_str}`;
      } else {
        return `${from_str}`;
      }
    },
    filters_amount() {
      const min = this.state_search_params.amount_min;
      const max = this.state_search_params.amount_max;
      if (min == max) {
        return min;
      }
      return null;
    },
    filters_amount_min() {
      const min = this.state_search_params.amount_min;
      const max = this.state_search_params.amount_max;
      if (min != max) {
        return min;
      }
      return null;
    },
    filters_amount_max() {
      const min = this.state_search_params.amount_min;
      const max = this.state_search_params.amount_max;
      if (min != max) {
        return max;
      }
      return null;
    },
    filters_exchange_assets_selected() {
      if (!this.state_search_params.exchange_asset_id) {
        return [];
      }
      return this.transactions_filters.exchange_assets_selected || [];
    },
    filters_exchange_assets_combined_options() {
      const selected = this.filters_exchange_assets_selected || [];
      const selectedIds = new Set(selected.map((item) => item.id));
      const options = this.filters_exchange_assets_options || [];

      return [
        ...selected,
        ...options.filter((item) => !selectedIds.has(item.id)),
      ];
    },
    sortedAddressMappings() {
      return this.transactions_filters.address_mappings || [];
    },
    filteredFromAddressMappings() {
      return this.sortedAddressMappings.filter((mapping) => {
        return (
          mapping.name?.toLowerCase().includes(this.searchFromAddress.toLowerCase()) ||
          mapping.address?.toLowerCase().includes(this.searchFromAddress.toLowerCase())
        );
      });
    },
    filteredToAddressMappings() {
      return this.sortedAddressMappings.filter((mapping) => {
        return (
          mapping.name?.toLowerCase().includes(this.searchToAddress.toLowerCase()) ||
          mapping.address?.toLowerCase().includes(this.searchToAddress.toLowerCase())
        );
      });
    },
  },
  methods: {
    handleCreateRule() {
      this.categorization_rules_menu_show = false;
      this.create_categorization_rule_dialog_open();
    },
    handleApplyAllRules() {
      this.categorization_rules_menu_show = false;
      this.categorization_rules_apply_all();
    },
    handleRulePreview(rule) {
      this.categorization_rules_menu_show = false;
      this.categorization_rule_preview(rule);
    },
    handleRuleApply(id) {
      this.categorization_rules_menu_show = false;
      this.categorization_rule_apply(id);
    },
    handleRuleDelete(id) {
      this.categorization_rule_delete(id);
    },
    exchange_accounts_select(selected) {
      if (!selected || selected.length == 0) {
        this.transactions_filter_add({ exchange_account_id: [] });
        return;
      }
      const ids = [...selected];
      let selected_last = ids.pop();
      if (selected_last?.id) {
        selected_last = selected_last.id;
      }
      ids.push(selected_last);

      this.transactions_filter_add({ exchange_account_id: ids });
    },
    assets_select(selected) {
      if (!selected || selected.length == 0) {
        this.transactions_filter_add({ asset_id: [] });
        return;
      }
      const ids = [...selected];
      let selected_last = ids.pop();
      if (selected_last?.id) {
        selected_last = selected_last.id;
      }
      ids.push(selected_last);

      this.transactions_filter_add({ asset_id: ids });
    },
    exchange_assets_select(selected) {
      if (!selected || selected.length == 0) {
        this.transactions_filter_add({ exchange_asset_id: [] });
        return;
      }
      // Handle both object and string formats
      const ids = selected.map(item => {
        if (typeof item === 'object' && item.id) {
          return item.id;
        }
        return item;
      });
      this.transactions_filter_add({ exchange_asset_id: ids });
    },
    exchange_assets_filter(val, update, abort) {
      this.filters_exchange_assets_search = val;

      if (val.length < 2) {
        update(() => {
          this.filters_exchange_assets_options = [];
        });
        return;
      }

      this.filters_exchange_assets_loading = true;

      const baseUrl = this.transactions_filters.action_path.replace('/transactions', '/transactions/exchange_assets_search');
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('q', val);

      fetch(url.toString(), {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then(response => response.json())
        .then(data => {
          update(() => {
            this.filters_exchange_assets_options = data.exchange_assets || [];
            this.filters_exchange_assets_loading = false;
          });
        })
        .catch(() => {
          abort();
          this.filters_exchange_assets_loading = false;
        });
    },
    kinds_select(selected) {
      if (!selected) {
        return this.transactions_filter_add({ kind: [] });
      }
      const ids = this.filters_kinds_selected.map(k => k.id);
      const id_existing_index = ids.findIndex(id => id == selected.id);
      if (id_existing_index === -1) {
        ids.push(selected.id);
      } else {
        ids.splice(id_existing_index, 1);
      }
      this.transactions_filter_add({ kind: ids });
    },
    kinds_clear(e) {
      e.stopPropagation();
      e.preventDefault();
      this.transactions_filter_add({
        kind: undefined,
        uncategorized: undefined,
      });
    },
    import_sources_select(selected) {
      if (!selected) {
        return this.transactions_filter_add({ import_source_id: [] });
      }
      const ids = this.filters_import_sources_selected.map(k => k.id);
      const id_existing_index = ids.findIndex(id => id == selected.id);
      if (id_existing_index === -1) {
        ids.push(selected.id);
      } else {
        ids.splice(id_existing_index, 1);
      }
      this.transactions_filter_add({ import_source_id: ids });
    },
    import_sources_clear(e) {
      e.stopPropagation();
      e.preventDefault();
      this.transactions_filter_add({
        import_source_id: [],
      });
    },
    date_range_select(range) {
      // Empty range (undefined).
      if (!range) {
        this.transactions_filter_add({
          date_from: undefined,
          date_to: undefined,
        });
        return;
      }

      // Range (object).
      if (range.from) {
        this.transactions_filter_add({
          date_from: range.from,
          date_to: range.to,
        });
        return;
      }

      // Day (string).
      this.transactions_filter_add({
        date_from: range,
        date_to: range,
      });
    },
    date_range_fiscal_year_select(fy) {
      this.date_range_select({
        from: qdate.formatDate(fy.date_start, "YYYY/MM/DD"),
        to: qdate.formatDate(fy.date_stop, "YYYY/MM/DD"),
      });
    },
    date_range_fiscal_years_active(fy) {
      const q_date_format = "YYYY/MM/DD";
      return !(
        qdate.formatDate(fy.date_start, q_date_format) == this.filters_date_range?.from &&
        qdate.formatDate(fy.date_stop, q_date_format) == this.filters_date_range?.to
      );
    },
    change_rates_select(cr) {
      this.transactions_filter_add({
        change_rate: cr?.value,
      });
    },
    balances_select(b) {
      this.transactions_filter_add({
        balance: b?.value,
      });
    },
    types_select(t) {
      this.transactions_filter_add({
        type: t?.value,
      });
    },
    manual_select(t) {
      this.transactions_filter_add({
        manual: t?.value,
      });
    },
    amount_min_update(value) {
      const min = this.state_search_params.amount_min;
      const max = this.state_search_params.amount_max;
      if (min == max) {
        this.transactions_filter_add({
          amount_min: value,
          amount_max: undefined,
        });
      } else {
        this.transactions_filter_add({
          amount_min: value,
        });
      }
    },
    amount_max_update(value) {
      const min = this.state_search_params.amount_min;
      const max = this.state_search_params.amount_max;
      if (min == max) {
        this.transactions_filter_add({
          amount_min: undefined,
          amount_max: value,
        });
      } else {
        this.transactions_filter_add({
          amount_max: value,
        });
      }
    },
  },
};
</script>

<style scoped>
.categorization-rules-list {
  padding: 0 4px;
}

.categorization-rule-item {
  padding-left: 0;
  padding-right: 0;
}

.categorization-rule-flow {
  gap: 8px;
}

.categorization-rule-name-chip {
  background: #f7f7f9;
  color: #111111;
  border-radius: 4px !important;
  font-weight: 400 !important;
  font-size: 12px !important;
  padding: 2px 12px !important;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.body--dark .categorization-rule-name-chip {
  background: #2c2c2c;
  color: #f5f5f5;

}

.categorization-rule-kind-chip {
  color: #111111;
  border-radius: 4px !important;
  font-weight: 400 !important;
  font-size: 12px !important;
  padding: 2px 12px !important;
}

.categorization-rule-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: nowrap;
}

.categorization-rules-divider {
  margin: 8px 0;
  width: 100%;
}
</style>
